import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatNzDate, nzDate } from '@promanage/core';
import { Chip } from '@promanage/ui';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, ListShell } from '../features/table/ListShell';
import { compareRows, type ColumnDef, type SortState } from '../features/table/columns';
import { CommunicationsNav } from '../features/lists/CommunicationsNav';
import { sendState } from '../features/lists/sendState';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/communications/sends/')({
  loader: ({ context }) => context.queryClient.prefetchQuery(sendsOptions()),
  component: SentEmailsPage,
});

const sendsOptions = () => trpc.bulkSend.list.queryOptions(undefined, { staleTime: 10_000 });

type Send = RouterOutputs['bulkSend']['list'][number];
type Column = 'subject' | 'kind' | 'progress' | 'skipped' | 'by' | 'when';
const NO_SENDS: Send[] = [];

const COLUMNS: readonly ColumnDef<Column, Send>[] = [
  {
    id: 'subject',
    label: 'Email',
    width: '24rem',
    fixed: true,
    defaultVisible: true,
    sortValue: (send) => send.subject.toLowerCase(),
    render: (send) => (
      <div className="min-w-0">
        <Link
          to="/communications/sends/$id"
          params={{ id: send.id }}
          className="block truncate font-semibold hover:text-accent hover:underline"
        >
          {send.subject}
        </Link>
        <span className="block truncate text-[0.75rem] text-ink-subtle">
          To {send.audience === 'tenants' ? 'tenants' : 'owners'}
          {send.listName ? ` · from “${send.listName}”` : ''}
        </span>
      </div>
    ),
  },
  {
    id: 'kind',
    label: 'Kind',
    width: '8rem',
    defaultVisible: true,
    sortValue: (send) => (send.essential ? 1 : 0),
    render: (send) => (
      <Chip tone={send.essential ? 'accent' : 'neutral'}>
        {send.essential ? 'Essential' : 'General'}
      </Chip>
    ),
  },
  {
    id: 'progress',
    label: 'Sent',
    width: '9rem',
    defaultVisible: true,
    sortValue: (send) => send.sent,
    render: (send) => {
      const state = sendState(send);
      return (
        <span className="flex items-center gap-2 tabular-nums">
          {send.sent}/{send.queued}
          <Chip tone={state.tone}>{state.label}</Chip>
        </span>
      );
    },
  },
  {
    id: 'skipped',
    label: 'Skipped',
    width: '6rem',
    align: 'right',
    defaultVisible: true,
    sortValue: (send) => send.skipped,
    render: (send) => <span className="tabular-nums">{send.skipped}</span>,
  },
  {
    id: 'by',
    label: 'By',
    width: '9rem',
    defaultVisible: true,
    sortValue: (send) => send.createdByName,
    render: (send) => <span className="block truncate">{send.createdByName}</span>,
  },
  {
    id: 'when',
    label: 'When',
    width: '7rem',
    defaultVisible: true,
    sortValue: (send) => send.createdAt,
    render: (send) => (
      <span className="tabular-nums">{formatNzDate(nzDate(new Date(send.createdAt)))}</span>
    ),
  },
];

/**
 * Every bulk email the agency has sent, newest first — and whether each has finished going out.
 */
function SentEmailsPage() {
  const navigate = useNavigate();
  const sends = useQuery({ ...sendsOptions(), meta: { errorTitle: 'Could not load sent emails' } });
  const all = sends.data ?? NO_SENDS;
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<Column>>({ column: 'when', direction: 'desc' });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((send) => !q || `${send.subject} ${send.listName ?? ''}`.toLowerCase().includes(q))
      .sort((a, b) => compareRows(a, b, sort, COLUMNS));
  }, [all, query, sort]);

  return (
    <ListShell
      title="Communications"
      subtitle={
        sends.isPending ? 'Loading…' : `${all.length} sent ${all.length === 1 ? 'email' : 'emails'}`
      }
      nav={<CommunicationsNav current="sends" />}
      actions={null}
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search subjects and lists"
      searchLabel="Search sent emails"
      isPending={sends.isPending}
      error={sends.error}
      onRetry={() => void sends.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState title={query ? `Nothing matches “${query}”` : 'Nothing sent yet'}>
          {query
            ? 'Try a different subject.'
            : 'Open a list, tick the rows to write to, and choose Email.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={COLUMNS}
        sort={sort}
        onSortChange={setSort}
        rowKey={(send) => send.id}
        onOpen={(send) =>
          void navigate({ to: '/communications/sends/$id', params: { id: send.id } })
        }
        caption={`Sent emails, one row per send. ${rows.length} shown.`}
        minWidth="52rem"
      />
    </ListShell>
  );
}
